import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

// Material ui
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  Paper
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

// Utils
import {
  formatPrice,
  formatToPercentage,
  safeDivision
} from '../../../../utils/format'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
// Components
import StoresTableHead from './components/StoresTableHead'
import { VisiblePerfectScrollbar } from '../GroupsTable/styles'
import { format } from 'date-fns'
import { BootstrapTooltip } from 'components/BootstrapTooltip'
import { UnavailableValue } from 'components/UnavailableValue'
import { ConversionValue } from 'components/ConversionValue'
import { getConversionRate } from 'utils/conversion'
import { dateWithNoTimezone } from 'utils/dateFunctions'
import {
  FUTURE_SALES_MESSAGE,
  LAST_YEAR_UNAVAILABLE_MESSAGE,
  getAccessibleUnavailableProps,
  getDisabledFieldTooltipProps,
  getSalesDataDisabledReason,
  getSalesDisabledFieldTooltipProps,
  isAggregatedReportSortDisabled,
  isFieldDisabled
} from 'utils/reportDisabledFields'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',

    '& .MuiTableHead-root th:nth-child(4), & .MuiTableHead-root th:nth-child(6)':
      {
        textAlign: 'right',
        paddingRight: '0px',
        minWidth: '80px'
      }
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  typographyPercent: {
    textAlign: 'right',
    fontSize: '11px',
    fontWeight: 'bold'
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1
  },
  lastYearVariationText: {
    fontSize: '11px',
    fontWeight: 'bold'
  },
  sticky: {
    position: 'sticky',
    left: 0,
    background: 'white'
  }
}))

const StoresTable = ({
  storesReport,
  handleSelectStore,
  disabledFields = {},
  referenceConversion
}) => {
  const classes = useStyles()
  // Lista filtrada pela busca chega sem storesInfos; aí a referência vem por prop
  const referenceConversionRate = getConversionRate(
    storesReport.storesInfos?.conversion ?? referenceConversion
  )
  const showConversion = (storesReport.stores || storesReport || []).some(
    store => store.conversion
  )
  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState('name')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedRow, setSelectedRow] = useState(-1)
  const acDayGoalReason = disabledFields.metric?.acDayGoal
  const mainGoalReason = disabledFields.metric?.mainGoal
  const projectionSoldReason = disabledFields.projection?.sold
  const salesDisabledReason = getSalesDataDisabledReason(disabledFields)
  const salesDisabled = isFieldDisabled(salesDisabledReason)
  const salesTooltipProps = getSalesDisabledFieldTooltipProps(disabledFields)
  const lastYearUnavailableMessage = salesDisabled
    ? FUTURE_SALES_MESSAGE
    : LAST_YEAR_UNAVAILABLE_MESSAGE
  const getStoreProjectionReason = store => projectionSoldReason ||
    (store.hasInputs === false ? 'NO_SALES_RECORDS' : undefined)

  useEffect(() => {
    if (isAggregatedReportSortDisabled(orderBy, disabledFields)) {
      setOrder('asc')
      setOrderBy('name')
    }
  }, [disabledFields, orderBy])

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1
    } else if (b[orderBy] > a[orderBy]) {
      return 1
    }
    return 0
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy)
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index])
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0])
      if (order !== 0) return order
      return a[1] - b[1]
    })
    return stabilizedThis.map(el => el[0])
  }

  const handleRequestSort = property => {
    if (isAggregatedReportSortDisabled(property, disabledFields)) return

    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = newPage => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = rows => {
    setRowsPerPage(rows)
    setPage(0)
  }

  const formattedStoresToStoresTable = () => {
    const stores = storesReport.stores || storesReport
    return stores?.length > 0
      ? stores.map(store => ({
          ...store,
          'average.tm': store.average.tm,
          'average.pv': store.average.pv,
          'average.pm': store.average.pm,
          'total.items': store.total.items,
          salesDay: store.daySold.sold,
          sold: store.total.sold,
          sales: store.total.sales,
          // Loja sem contagem vai para o fim na ordenação decrescente
          conversionRate: getConversionRate(store.conversion) ?? -1,
          items: store.total.items.toFixed(),
          goalPercent: salesDisabled || isFieldDisabled(acDayGoalReason)
            ? 0
            : Number(
              (safeDivision(store.total.sold, store.metric.acDayGoal) * 100)
                .toFixed(2)
            ),
          acDayGoal: store.metric.acDayGoal,
          mainGoal: store.metric.mainGoal,
          projection: store.hasInputs === false ? 0 : store.projection.sold,
          mainGoalPercent: salesDisabled || isFieldDisabled(mainGoalReason)
            ? 0
            : Number(
              (safeDivision(store.total.sold, store.metric.mainGoal) * 100)
                .toFixed(2)
            )
        }))
      : []
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer>
          <VisiblePerfectScrollbar>
            <Table
              className={classes.table}
              aria-labelledby='tableTitle'
              aria-label='enhanced table'
              stickyHeader
              onMouseLeave={() => {
                if (selectedRow > -1) {
                  setSelectedRow(-1)
                }
              }}
            >
              <StoresTableHead
                classes={classes}
                disabledFields={disabledFields}
                showConversion={showConversion}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={formattedStoresToStoresTable().length}
                onHoverHeader={() => {
                  if (selectedRow > -1) {
                    setSelectedRow(-1)
                  }
                }}
              />
              <TableBody>
                {!formattedStoresToStoresTable() ||
                formattedStoresToStoresTable().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showConversion ? 16 : 15} />
                  </TableRow>
                ) : (
                  <>
                    {storesReport.storesInfos && (
                      <TableRow
                        hover
                        key={0}
                        selected
                        onMouseEnter={() => {
                          if (selectedRow > -1) {
                            setSelectedRow(-1)
                          }
                        }}
                      >
                        <TableCell
                          className={classes.sticky}
                          style={{ backgroundColor: '#fdf8ec' }}
                        >
                          <b>Todas Lojas</b>
                        </TableCell>
                        <TableCell {...salesTooltipProps}>
                          <b>
                            {salesDisabled
                              ? '-'
                              : formatPrice(storesReport.storesInfos.daySold.sold)}
                          </b>
                        </TableCell>
                        <TableCell>
                          <b>{formatPrice(storesReport.dayGoals.sell)}</b>
                        </TableCell>
                        <TableCell {...salesTooltipProps}>
                          <b>
                            {salesDisabled
                              ? '-'
                              : formatPrice(storesReport.storesInfos.total.sold)}
                          </b>
                        </TableCell>
                        <TableCell
                          {...(salesDisabled
                            ? salesTooltipProps
                            : getDisabledFieldTooltipProps(acDayGoalReason))}
                          padding='none'
                          style={{ paddingRight: 0, textAlign: 'end' }}
                        >
                          <b>
                            {salesDisabled || isFieldDisabled(acDayGoalReason)
                              ? '-'
                              : formatToPercentage(
                                  safeDivision(
                                    storesReport.storesInfos.total.sold,
                                    storesReport.storesInfos.metric.acDayGoal
                                  ) * 100
                                )}
                          </b>
                        </TableCell>
                        <TableCell {...getDisabledFieldTooltipProps(acDayGoalReason)}>
                          <b>
                            {isFieldDisabled(acDayGoalReason)
                              ? '-'
                              : formatPrice(
                                  storesReport.storesInfos.metric.acDayGoal
                                )}
                          </b>
                        </TableCell>
                        <TableCell
                          {...(salesDisabled
                            ? salesTooltipProps
                            : getDisabledFieldTooltipProps(mainGoalReason))}
                          style={{ paddingRight: 0, textAlign: 'end' }}
                        >
                          <b>
                            {salesDisabled || isFieldDisabled(mainGoalReason)
                              ? '-'
                              : formatToPercentage(
                                  safeDivision(
                                    storesReport.storesInfos.total.sold,
                                    storesReport.storesInfos.metric.mainGoal
                                  ) * 100
                                )}
                          </b>
                        </TableCell>
                        <TableCell {...getDisabledFieldTooltipProps(mainGoalReason)}>
                          <b>
                            {isFieldDisabled(mainGoalReason)
                              ? '-'
                              : formatPrice(
                                  storesReport.storesInfos.metric.mainGoal
                                )}
                          </b>
                        </TableCell>
                        <TableCell {...getDisabledFieldTooltipProps(projectionSoldReason)}>
                          <b>
                            {isFieldDisabled(projectionSoldReason)
                              ? '-'
                              : formatPrice(
                                  storesReport.storesInfos.projection.sold
                                )}
                          </b>
                        </TableCell>
                        {showConversion && (
                          <TableCell>
                            <ConversionValue
                              variant='badge'
                              conversion={storesReport.storesInfos.conversion}
                              salesDisabled={salesDisabled}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography
                            style={{
                              color:
                                !salesDisabled &&
                                typeof storesReport.storesInfos.lastYearVariation ===
                                'number'
                                  ? (value =>
                                      value > 0
                                        ? '#59ac2a'
                                        : value > -10
                                        ? '#f0b222'
                                        : '#f34455')(
                                      storesReport.storesInfos.lastYearVariation
                                    )
                                  : '#757575'
                            }}
                            className={classes.lastYearVariationText}
                          >
                            {!salesDisabled && typeof storesReport.storesInfos.lastYearVariation === 'number'
                              ? formatToPercentage(
                                  storesReport.storesInfos.lastYearVariation
                                )
                              : <UnavailableValue message={lastYearUnavailableMessage} />}
                          </Typography>
                        </TableCell>
                        <TableCell {...salesTooltipProps}>
                          <b>
                            {salesDisabled
                              ? '-'
                              : storesReport.storesInfos.total.sales
                              ? storesReport.storesInfos.total.sales
                              : 0}
                          </b>
                        </TableCell>
                        <TableCell {...salesTooltipProps}>
                          <b>
                            {salesDisabled
                              ? '-'
                              : formatPrice(storesReport.storesInfos.average.tm)}
                          </b>
                        </TableCell>
                        <TableCell {...salesTooltipProps}>
                          <b>
                            {salesDisabled
                              ? '-'
                              : storesReport.storesInfos.average.pv.toFixed(2)}
                          </b>
                        </TableCell>
                        <TableCell {...salesTooltipProps}>
                          <b>
                            {salesDisabled
                              ? '-'
                              : formatPrice(storesReport.storesInfos.average.pm)}
                          </b>
                        </TableCell>
                        <TableCell {...salesTooltipProps}>
                          <b>
                            {salesDisabled
                              ? '-'
                              : storesReport.storesInfos.total.items
                              ? storesReport.storesInfos.total.items
                              : 0}
                          </b>
                        </TableCell>
                      </TableRow>
                    )}
                    {stableSort(
                      formattedStoresToStoresTable(),
                      getComparator(
                        order,
                        isAggregatedReportSortDisabled(orderBy, disabledFields)
                          ? 'name'
                          : orderBy
                      )
                    )
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((store, index) => {
                        return (
                          <TableRow
                            hover
                            onClick={() => handleSelectStore(store._id)}
                            tabIndex={-1}
                            key={store.name}
                            style={{ cursor: 'pointer' }}
                            data-cy='store-row'
                            onMouseEnter={() => setSelectedRow(index)}
                          >
                            <TableCell
                              scope='row'
                              className={classes.sticky}
                              style={{
                                ...(index === selectedRow && {
                                  backgroundColor: '#f5f5f5'
                                })
                              }}
                            >
                              {store.name}
                            </TableCell>
                            <TableCell
                              {...salesTooltipProps}
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              {salesDisabled ? '-' : formatPrice(store.daySold.sold)}
                              {!salesDisabled && store.daySold.updatedAt && (
                                <BootstrapTooltip
                                  title={`Ultima atualização: ${format(
                                    dateWithNoTimezone(store.daySold.updatedAt),
                                    'pp'
                                  )}`}
                                >
                                  <ErrorOutlineIcon
                                    style={{
                                      color: '#C4C4C4',
                                      fontSize: 20,
                                      marginLeft: 'auto'
                                    }}
                                  />
                                </BootstrapTooltip>
                              )}
                            </TableCell>
                            <TableCell>{formatPrice(store.dayGoals.sell)}</TableCell>
                            <TableCell {...salesTooltipProps}>
                              {salesDisabled ? '-' : formatPrice(store.sold)}
                            </TableCell>
                            <TableCell
                              style={{ paddingRight: 0 }}
                              {...(salesDisabled
                                ? salesTooltipProps
                                : getDisabledFieldTooltipProps(acDayGoalReason))}
                            >
                              <Typography
                                style={{
                                  color: salesDisabled || isFieldDisabled(acDayGoalReason)
                                    ? 'inherit'
                                    : (value =>
                                        value >= 100
                                          ? '#27ae60'
                                          : value >= 80
                                          ? '#f39c12'
                                          : '#e74c3c')(
                                        safeDivision(
                                          store.sold,
                                          store.metric.acDayGoal
                                        ) * 100
                                      )
                                }}
                                className={classes.typographyPercent}
                              >
                                {salesDisabled || isFieldDisabled(acDayGoalReason)
                                  ? '-'
                                  : store.metric.acDayGoal !== 0
                                  ? +(
                                      safeDivision(
                                        store.sold,
                                        store.metric.acDayGoal
                                      ) * 100
                                    ).toFixed(2) + '%'
                                  : '0%'}
                              </Typography>
                            </TableCell>
                            <TableCell {...getDisabledFieldTooltipProps(acDayGoalReason)}>
                              {isFieldDisabled(acDayGoalReason)
                                ? '-'
                                : formatPrice(store.acDayGoal)}
                            </TableCell>
                            <TableCell
                              style={{ paddingRight: 0 }}
                              {...(salesDisabled
                                ? salesTooltipProps
                                : getDisabledFieldTooltipProps(mainGoalReason))}
                            >
                              <Typography
                                style={{
                                  color: salesDisabled || isFieldDisabled(mainGoalReason)
                                    ? 'inherit'
                                    : (value =>
                                        value >= 100
                                          ? '#27ae60'
                                          : value >= 80
                                          ? '#f39c12'
                                          : '#e74c3c')(
                                        safeDivision(
                                          store.sold,
                                          store.metric.mainGoal
                                        ) * 100
                                      )
                                }}
                                className={classes.typographyPercent}
                              >
                                {salesDisabled || isFieldDisabled(mainGoalReason)
                                  ? '-'
                                  : store.metric.mainGoal !== 0
                                  ? +(
                                      safeDivision(
                                        store.sold,
                                        store.metric.mainGoal
                                      ) * 100
                                    ).toFixed(2) + '%'
                                  : '0%'}
                              </Typography>
                            </TableCell>
                            <TableCell {...getDisabledFieldTooltipProps(mainGoalReason)}>
                              {isFieldDisabled(mainGoalReason)
                                ? '-'
                                : formatPrice(store.mainGoal)}
                            </TableCell>
                            <TableCell {...getDisabledFieldTooltipProps(
                              getStoreProjectionReason(store)
                            )}>
                              {isFieldDisabled(getStoreProjectionReason(store))
                                ? '-'
                                : formatPrice(store.projection)}
                            </TableCell>
                            {showConversion && (
                              <TableCell>
                                <ConversionValue
                                  conversion={store.conversion}
                                  referenceRate={referenceConversionRate}
                                  salesDisabled={salesDisabled}
                                />
                              </TableCell>
                            )}
                            <TableCell {...(salesDisabled
                              ? salesTooltipProps
                              : typeof store.lastYearVariation !== 'number'
                                ? getAccessibleUnavailableProps(lastYearUnavailableMessage)
                                : {})}>
                              <Typography
                                style={{
                                  color: !salesDisabled && typeof store.lastYearVariation === 'number'
                                      ? (value =>
                                          value > 0
                                            ? '#59ac2a'
                                            : value > -10
                                            ? '#f0b222'
                                            : '#f34455')(store.lastYearVariation)
                                      : '#757575'
                                }}
                                className={classes.lastYearVariationText}
                              >
                                {!salesDisabled && typeof store.lastYearVariation === 'number' ? formatToPercentage(store.lastYearVariation) : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell {...salesTooltipProps}>
                              {salesDisabled ? '-' : `${store ? store.total.sales : 0}`}
                            </TableCell>
                            <TableCell {...salesTooltipProps}>
                              {salesDisabled ? '-' : formatPrice(store.average.tm)}
                            </TableCell>
                            <TableCell {...salesTooltipProps}>
                              {salesDisabled ? '-' : store.average.pv.toFixed(2)}
                            </TableCell>
                            <TableCell {...salesTooltipProps}>
                              {salesDisabled ? '-' : formatPrice(store.average.pm)}
                            </TableCell>
                            <TableCell {...salesTooltipProps}>
                              {salesDisabled ? '-' : `${store ? store.total.items : 0}`}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                  </>
                )}
              </TableBody>
            </Table>
          </VisiblePerfectScrollbar>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={formattedStoresToStoresTable().length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, page) => handleChangePage(page)}
          onRowsPerPageChange={event => {
            handleChangeRowsPerPage(Number(event.target.value))
          }}
        />
      </Paper>
    </div>
  )
}

StoresTable.propTypes = {
  formattedStoresToStoresTable: PropTypes.array,
  disabledFields: PropTypes.object,
  handleSelectStore: PropTypes.func,
  referenceConversion: PropTypes.object
}

export default StoresTable
